import axios from './axiosInstance';
import axiosLib from 'axios';
import { APIForm, APIFormTemplate } from '../../types';

interface SportAreaParams {
  sport: string;
  areaId: string;
}

interface FormMutationParams extends SportAreaParams {
  form: APIForm;
}

interface CreateFormParams extends FormMutationParams {
  /** `x-request-id`, for a create that must not happen twice (the onboarding-pack install). */
  requestId?: string;
}

interface GetFormParams extends SportAreaParams {
  formId: string;
}

interface GetFormsParams extends SportAreaParams {
  includeUllrDefaults?: boolean;
  formType?: string;
  abortSignal?: AbortSignal;
}

interface EnableDefaultFormsParams extends SportAreaParams {
  body: Record<string, any>;
}

interface DeleteFormParams extends SportAreaParams {
  formId: string;
}

const createForm = ({ sport, areaId, form, requestId }: CreateFormParams): Promise<APIForm> => {
  let url = `/${sport}/areas/${areaId}/forms`;
  const request = requestId
    ? axios.put(url, form, { headers: { 'x-request-id': requestId } })
    : axios.put(url, form);
  return request
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
}

const updateForm = ({ sport, areaId, form }: FormMutationParams): Promise<APIForm> => {
  let url = `/${sport}/areas/${areaId}/forms/${form.id}`;
  return axios.post(url, form)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
}

const getForm = ({ sport, areaId, formId }: GetFormParams): Promise<APIForm> => {
  let url = `/${sport}/areas/${areaId}/forms/${formId}`;
  return axios.get(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const getForms = ({ sport, areaId, includeUllrDefaults, formType, abortSignal }: GetFormsParams): Promise<APIForm[]> => {
  const CancelToken = axiosLib.CancelToken;
  const source = CancelToken.source();
  if (abortSignal) {
    abortSignal.addEventListener('abort', () => {
      source.cancel('Operation canceled by the user.');
    });
  }

  let url = `/${sport}/areas/${areaId}/forms`;
  if (formType) {
    url += `/${formType}`;
  } else if (includeUllrDefaults !== undefined) {
    url += `?includeUllrDefaults=${includeUllrDefaults}`
  }
  return axios.get(url, { cancelToken: source.token })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const getDefaultForms = ({ sport, areaId }: SportAreaParams): Promise<APIForm[]> => {
  let url = `/${sport}/areas/${areaId}/forms/defaults`;
  return axios.get(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const enableDefaultForms = ({ sport, areaId, body }: EnableDefaultFormsParams): Promise<any> => {
  let url = `/${sport}/areas/${areaId}/forms/defaults`;
  return axios.post(url, body)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const getFormTemplates = ({ sport, areaId }: SportAreaParams): Promise<APIFormTemplate[]> => {
  const url = `/${sport}/areas/${areaId}/forms/templates`;
  return axios.get(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const deleteForm = ({ sport, areaId, formId }: DeleteFormParams): Promise<void> => {
  let url = `/${sport}/areas/${areaId}/forms/${formId}`;
  return axios.delete(url)
    .then(() => {})
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const formFunctions = {
  createForm,
  updateForm,
  getForm,
  getForms,
  getDefaultForms,
  getFormTemplates,
  enableDefaultForms,
  deleteForm,
};

export default formFunctions;
